<template>
    <div class="adobe-pdf">
        <div id="adobe-dc-view" style="height: 560px; width: 100%" v-loading="loading"></div>
        <p v-if="error" class="adobe-pdf__error">{{ error }}</p>
    </div>
</template>
<script>
import axios from "axios";

/** Certificate addresses the API will not answer without a token. */
const NEEDS_TOKEN = /\/(downloadCourseCertificate|downloadCourseCertificateByEntry|downloadAllCourseCertificate|downloadCourseFoldersCertificate)\//;

export default {
    name: "adobe-pdf",
    props: {
        path: String,
        url: String,
        fullUrl: String
    },
    data() {
        return {
            loading: false,
            error: ""
        };
    },
    mounted() {
        this.loading = true;
        // The SDK announces itself once per page. On a second viewer that
        // announcement has already been and gone, so waiting for it again
        // would leave the reader spinning for ever: if the SDK is already
        // here, start.
        if (window.AdobeDC) {
            this.getPdf();
            return;
        }
        document.addEventListener("adobe_dc_view_sdk.ready", this.getPdf, { once: true });
        this.$loadScript("https://documentservices.adobe.com/view-sdk/viewer.js")
            .catch(() => this.fail("The document reader could not be loaded."));
    },
    beforeDestroy() {
        document.removeEventListener("adobe_dc_view_sdk.ready", this.getPdf);
    },
    methods: {
        getPdfSourceUrl() {
            if (this.fullUrl) {
                if (
                    this.fullUrl.startsWith("http://") ||
                    this.fullUrl.startsWith("https://") ||
                    this.fullUrl.startsWith("blob:") ||
                    this.fullUrl.startsWith("data:")
                ) {
                    return this.fullUrl;
                }
                return `${process.env.VUE_APP_API_URL}/${this.fullUrl}`.replace(/([^:]\/)\/+/g, "$1");
            }
            return `${process.env.VUE_APP_API_URL}/${this.path}/${this.url}`.replace(/([^:]\/)\/+/g, "$1");
        },
        /**
         * What to hand the reader.
         *
         * Left to itself the reader fetches the address from the browser, with
         * no Authorization header of its own. A certificate address answers
         * that by redirecting to the app, so the reader is handed a page of
         * HTML and sits there for ever. A blob: address is no better: the
         * reader asks for it in ranges, which an object URL does not always
         * answer.
         *
         * So for both of those we read the file ourselves and give the reader
         * the bytes. Everything else it can fetch as it always has.
         */
        pdfContent(url) {
            const isBlob = url.startsWith("blob:");
            if (!isBlob && !NEEDS_TOKEN.test(url)) {
                return { location: { url } };
            }

            const token = localStorage.getItem("hot-token");
            const bytes = isBlob
                ? fetch(url).then(resp => resp.arrayBuffer())
                : axios
                    .get(url, {
                        responseType: "arraybuffer",
                        headers: token ? { Authorization: "Bearer " + token } : {}
                    })
                    .then(resp => resp.data);

            return {
                promise: bytes.catch(e => {
                    this.fail("This certificate could not be opened. Please try again.");
                    throw e;
                })
            };
        },
        getPdf() {
            if (!window.AdobeDC) {
                this.fail("The document reader could not be loaded.");
                return;
            }
            this.error = "";
            let adobeDCView = new AdobeDC.View({
                clientId: process.env.VUE_APP_ADOBE_PDF_CLIENT_ID,
                divId: "adobe-dc-view"
            });
            let pdfEmbedOptions = {
                embedMode: "SIZED_CONTAINER",
                showAnnotationTools: false,
                showDownloadPDF: false,
                showPrintPDF: false
            };
            // The reader draws its own progress inside this container, so the
            // page's own veil comes off as soon as it has been handed the
            // file: leaving it on would cover whatever the reader draws.
            this.loading = false;
            Promise.resolve(
                adobeDCView.previewFile(
                    {
                        content: this.pdfContent(this.getPdfSourceUrl()),
                        metaData: { fileName: this.url || "document.pdf" }
                    },
                    pdfEmbedOptions
                )
            ).catch(() => this.fail("This document could not be opened. Please try again."));
        },
        fail(message) {
            this.loading = false;
            this.error = message;
        }
    }
};
</script>
<style>
.adobe-pdf__error {
    margin: 0.75rem 0 0;
    text-align: center;
    color: #b91c1c;
    font-size: 0.875rem;
}

@media only screen and (max-width: 1024px),
(min-device-width: 768px) and (max-device-width: 1024px) {
    #adobe-dc-view{
        height: 100% !important;
    }
}

</style>
